/**
 * i18n - deliberately no library.
 *
 * Two locales, ~60 strings, chosen once from the browser and never switched
 * mid-session. i18next would be ~40KB gzipped on the screen §11 says decides
 * the funnel, to solve problems this app does not have (plurals beyond one
 * case, namespaces, lazy-loaded bundles, runtime language switching).
 *
 * German first because v1 is Vienna. Anything unrecognised falls back to
 * English rather than showing a key.
 */

export type Locale = 'en' | 'de'

const STRINGS = {
  // ── create ────────────────────────────────────────────────────────────
  // The wordmark stays; the headline sells the point, not the mechanic.
  'create.title': { en: 'Sorted', de: 'Sorted' },
  // Says what the product DOES, not what it saves you from. "Skip the
  // back-and-forth" named the annoyance and left the reader to work out what
  // arrives instead; this states the promise, and the pitch under it explains
  // how. "We" is honest here - the ranking picks the options, and the
  // recipient only taps.
  'create.headline': {
    en: 'We decide the where.',
    de: 'Wir entscheiden das Wo.',
  },
  // The question someone arrives with when a friend sends them a bare link.
  // Naming the product answers it before the sentence under it explains it.
  'create.whatIsThis': { en: 'What is sorted.cx?', de: 'Was ist sorted.cx?' },
  // "How does it know these places" is the obvious next question, and Google's
  // terms require the attribution regardless. One line, under the pitch.
  'create.dataSource': {
    en: 'Suggestions are based on Google Maps data — ratings, opening hours and travel times.',
    de: 'Die Vorschläge basieren auf Google-Maps-Daten — Bewertungen, Öffnungszeiten und Fahrzeiten.',
  },
  // ── create: the two steps ─────────────────────────────────────────────
  // The pitch explains the whole mechanic before asking for anything: the
  // first screen is the only place someone decides whether this is worth a
  // tap at all.
  //
  // Deliberately not a number of places. A long corridor can yield fewer than
  // five, and invariant 9 forbids ever showing "nothing found" - so a fixed
  // count is a promise the product cannot always keep. "Passende Orte
  // zwischen euch" stays true whether the pipeline returns three or fifteen.
  'create.pitch': {
    en: 'Send one link. The recipient sees places, picks one, and you get the confirmation.',
    de: 'Sende einen Link. Der Empfänger sieht passende Orte, wählt einen aus - und du bekommst die Bestätigung.',
  },
  // Step labels for the indicator - one word each; they sit under 3px bars.
  'create.stepWhat': { en: 'What', de: 'Was' },
  'create.stepWhen': { en: 'When', de: 'Wann' },
  'create.stepWhere': { en: 'Where', de: 'Wo' },
  'create.step3': { en: 'Where should you meet?', de: 'Wo wollt ihr euch treffen?' },
  'create.step1': { en: 'What do you want to do?', de: 'Was wollt ihr machen?' },
  'create.step2': { en: 'When?', de: 'Wann wollt ihr euch treffen?' },
  'create.step2Hint': {
    en: 'Pick the day and time that suits you.',
    de: 'Wähle Tag und Uhrzeit, die dir passen.',
  },
  'create.step3Hint': {
    en: 'Somewhere fair between you, or a spot you already have in mind.',
    de: 'Fair zwischen euch - oder eine Gegend, die du schon im Kopf hast.',
  },
  'create.areaBetween': { en: 'Between you (based on your locations)', de: 'Zwischen euch (basierend auf euren Standorten)' },
  'create.areaPinned': { en: 'Pick it on the map', de: 'Auf der Karte auswählen' },
  // What the two actually do, in the terms that matter: one lets travel time
  // decide, the other overrides it. Said before the tap, not after.
  'create.areaHint': {
    en: '“Between you” lets the travel times decide. Or point at a spot on the map and we look there instead - nearby if it’s quiet.',
    de: '„Zwischen euch“ entscheidet nach Fahrzeit. Oder zeig auf der Karte, wo - dann suchen wir dort, und in der Nähe, falls es dort wenig gibt.',
  },
  'create.areaChange': { en: 'Change', de: 'Ändern' },
  'create.areaTitle': { en: 'Which area?', de: 'Welche Gegend?' },
  'create.areaSearchHint': {
    en: 'Move the map so the pin sits where you want to meet.',
    de: 'Verschiebe die Karte, bis der Pin dort ist, wo ihr euch treffen wollt.',
  },
  'create.originTitle': {
    en: 'Where will you set off from?',
    de: 'Von wo fährst du los?',
  },
  'create.originHint': {
    en: 'We compare travel times from here, so pick where you’ll actually set off - that’s what shows you both what the trip costs.',
    de: 'Wir vergleichen die Entfernungen von hier aus. Nimm also, wo du wirklich losfährst - so seht ihr beide, wie weit es ist.',
  },
  'create.next': { en: 'Next', de: 'Weiter' },

  'create.working': { en: 'Creating your link…', de: 'Link wird erstellt…' },
  'create.privacy': {
    en: 'We use a rough area to judge travel time. No account, no app.',
    de: 'Wir nutzen nur eine ungefähre Gegend für die Fahrzeit. Kein Konto, keine App.',
  },
  'create.timePast': {
    en: 'That time has already passed - pick a later one.',
    de: 'Dieser Zeitpunkt ist schon vorbei - wähle einen späteren.',
  },
  'create.failed': { en: 'Could not create that. Try again?', de: 'Hat nicht geklappt. Nochmal?' },

  // ── share ─────────────────────────────────────────────────────────────
  'share.title': { en: 'Ready to send', de: 'Bereit zum Senden' },
  // The count is interpolated: it was written into this sentence as "3" and
  // went stale the moment the product showed five, promising less than it
  // delivered.
  'share.body': {
    en: 'The recipient will get up to {n} suggestions. You’ll get a message when it’s sorted.',
    de: 'Der Empfänger bekommt bis zu {n} Vorschläge. Du bekommst Bescheid, sobald es steht.',
  },
  'share.whatsapp': { en: 'Send on WhatsApp', de: 'Per WhatsApp senden' },
  'share.copy': { en: 'Copy link', de: 'Link kopieren' },
  'share.startOver': { en: 'Start over', de: 'Neu anfangen' },
  'share.copied': { en: 'Copied', de: 'Kopiert' },
  // Carries the WHEN. An invite without it starts the negotiation this
  // product exists to end - the recipient's first reply would be when?.
  'share.invite': {
    en: '{activity} on {when}? Pick a spot that works for you:',
    de: '{activity} am {when}? Such dir einen Ort aus, der für dich passt:',
  },
  /**
   * The link does two jobs, and only one of them is obvious. Framing it as
   * "opening it shows your side" explained a mechanic; saying what it is FOR
   * - checking whether they have decided - makes it useful rather than
   * confusing.
   */
  'share.ownLinkHint': {
    en: 'Open it any time to see whether the recipient has picked a place.',
    de: 'Öffne ihn jederzeit, um zu sehen, ob der Empfänger schon einen Ort gewählt hat.',
  },

  // ── origin ────────────────────────────────────────────────────────────
  // Bob arrives from a link with no context at all - this is the only thing
  // that tells him what happened and why he is here, so it says it plainly
  // rather than as a small caption.
  'origin.invited': {
    en: 'Someone sent you this link because they want to meet up',
    de: 'Du hast diesen Link von jemandem erhalten, der dich treffen möchte',
  },
  // The same sentence, carrying the area the creator pinned. One statement
  // instead of two: the invitation and where it is are the same fact, and
  // saying the area again three lines later only repeated it.
  //
  // In two halves, because the area is emphasised and the languages put it in
  // different places — English at the end, German mid-clause. Splitting the
  // string keeps markup out of the translation table.
  // Three parts, because BOTH the activity and the area are emphasised and
  // the two languages order them differently: German puts the activity right
  // before the verb ("dich zum Brunch in X treffen möchte"), English puts it
  // after ("meet you for brunch in X").
  'origin.invitedInBefore': {
    en: 'Someone sent you this link — they want to meet you for',
    de: 'Du hast diesen Link von jemandem erhalten, der dich zum',
  },
  /** Between the activity and the area. */
  'origin.invitedInMiddle': { en: 'in', de: 'in' },
  /** After the area. Never empty - the coverage test cannot tell a deliberate
   *  empty string from a forgotten one. */
  'origin.invitedInAfter': { en: 'to meet up', de: 'treffen möchte' },
  // The same sentence without a pinned area: the corridor decides where.
  'origin.invitedForBefore': {
    en: 'Someone sent you this link — they want to meet you for',
    de: 'Du hast diesen Link von jemandem erhalten, der dich zum',
  },
  'origin.invitedForAfter': { en: 'somewhere', de: 'treffen möchte' },
  'origin.question': { en: 'Where will you travel from?', de: 'Von wo kommst du?' },
  'origin.hint': {
    en: 'So we can find a place that’s a fair trip for both of you - roughly halfway, not across town.',
    de: 'Damit wir einen Ort finden, der für euch beide fair liegt - ungefähr auf halbem Weg, nicht quer durch die Stadt.',
  },
  'origin.privacy': {
    en: 'We only use an approximate area, and never show it to anyone.',
    de: 'Wir nutzen nur eine ungefähre Gegend und zeigen sie niemandem.',
  },
  'origin.searching': { en: 'Looking for somewhere…', de: 'Wir suchen etwas…' },
  'origin.failed': { en: 'Could not work that out. Try again?', de: 'Hat nicht geklappt. Nochmal?' },

  // ── origin picker ─────────────────────────────────────────────────────
  // "Ich gehe von hier los" reads as "from this exact spot" and confuses more
  // than it explains. The button captures the current position - say that.
  'picker.gps': { en: 'My current location', de: 'Mein derzeitiger Standort' },
  'picker.locating': { en: 'Finding you…', de: 'Wir suchen dich…' },
  'picker.elsewhere': { en: 'Somewhere else?', de: 'Woanders?' },
  'picker.useGps': { en: 'Use my location instead', de: 'Doch meinen Standort nutzen' },
  'area.naming': { en: 'Finding out where that is…', de: 'Wo ist das…' },
  // The pin is the area; the name is a courtesy. When OSM has nothing to say
  // about a spot, that is not an error - so this says what was chosen rather
  // than what failed.
  'area.unnamed': { en: 'The spot you picked', de: 'Die Stelle, die du gewählt hast' },
  'area.confirm': { en: 'Meet around here', de: 'Hier in der Gegend treffen' },
  'area.locate': { en: 'Go to my location', de: 'Zu meinem Standort' },
  'area.zoomIn': { en: 'Zoom in', de: 'Näher ran' },
  'area.zoomOut': { en: 'Zoom out', de: 'Weiter weg' },

  'picker.search': { en: 'Station, district or landmark…', de: 'Station, Bezirk oder Ort…' },
  'picker.noMatch': {
    en: 'No match. Try a station name, or use your location.',
    de: 'Nichts gefunden. Probier eine Station oder deinen Standort.',
  },
  'picker.denied': {
    en: 'No problem - type where you’re coming from.',
    de: 'Kein Problem - tipp ein, von wo du kommst.',
  },
  'picker.unavailable': {
    en: 'Could not find you - type where you’re coming from.',
    de: 'Wir konnten dich nicht finden - tipp ein, von wo du kommst.',
  },
  // Shown when a remembered origin has no name - GPS gives coordinates, not a
  // label. "The usual place" said nothing; say what it actually is.
  'picker.usual': { en: 'Where you were last time', de: 'Wie beim letzten Mal' },
  'picker.change': { en: 'change', de: 'ändern' },

  // ── suggestion ────────────────────────────────────────────────────────
  'venue.noPhoto': { en: 'No image available', de: 'Kein Bild verfügbar' },
  'suggest.you': { en: 'You', de: 'Du' },
  'suggest.them': { en: 'Them', de: 'Die anderen' },
  // The arrow is a ChevronRight icon now, not part of the string.
  // States what is on offer rather than instructing. "Pick one" told him to
  // do the obvious thing; the count is the useful part — it says the list is
  // short and ends, which is the whole promise of this screen.
  //
  // Interpolated, not hardcoded: a thin area can yield two, and a screen that
  // promises three while showing two is the kind of small lie that makes the
  // rest of the page less believable.
  'suggest.pickOne': {
    en: 'We have {n} suggestions for you',
    de: 'Wir haben {n} Vorschläge für dich',
  },
  'suggest.pickOneSingle': {
    en: 'We have one suggestion for you',
    de: 'Wir haben einen Vorschlag für dich',
  },
  'suggest.thisOne': { en: 'This one', de: 'Das auswählen' },
  // Where the options came from. The corridor case says it in terms of the
  // benefit ("fair"), not the mechanism - nobody asked for a midpoint.
  'suggest.between': {
    en: 'Fairly placed between the two of you.',
    de: 'Fair zwischen euch beiden gelegen.',
  },
  'suggest.outsideArea': { en: 'just outside', de: 'knapp daneben' },
  // The terminal exit, not another round. Every path must end in a decision
  // or a human handoff (invariant 9) - this is the handoff, said as the
  // recipient would say it.
  'suggest.selfServe': { en: 'I’ll look myself, thanks', de: 'Ich schau selbst, danke' },
  'suggest.onMaps': { en: 'On Maps', de: 'Auf Maps' },
  'suggest.lookUp': { en: 'Look it up', de: 'Nachschauen' },
  'suggest.back': { en: 'Back to the last one', de: 'Zurück zum vorigen' },
  'suggest.narrowTitle': { en: 'Best we’ve got.', de: 'Das Beste, was wir haben.' },
  'suggest.goodEnough': { en: 'Good enough', de: 'Passt schon' },
  'suggest.failed': { en: 'That didn’t go through. Try again?', de: 'Hat nicht geklappt. Nochmal?' },
  'suggest.nothing': {
    en: 'We couldn’t find anywhere this time.',
    de: 'Diesmal haben wir nichts gefunden.',
  },
  'suggest.nothingHint': {
    en: 'Message them directly and sort it out between you.',
    de: 'Schreib direkt und macht es untereinander aus.',
  },


  // ── fairness ──────────────────────────────────────────────────────────
  // Replaces the two precise ETAs. Those were transit-only, so they were
  // simply wrong for anyone driving or cycling - and a 13-minute gap that is
  // an artifact of the travel mode reads as unfairness that is not there.
  // The claim stays (that is §7's fairness proof); the false precision goes.
  'fair.balanced': {
    en: 'About the same trip for both of you',
    de: 'Für euch beide etwa gleich weit',
  },
  'fair.slightlyYou': {
    en: 'Roughly halfway - a little closer to you',
    de: 'Ungefähr auf halbem Weg - etwas näher bei dir',
  },
  'fair.slightlyThem': {
    en: 'Roughly halfway - a little closer to them',
    de: 'Ungefähr auf halbem Weg - etwas näher beim Gegenüber',
  },
  'fair.detail': {
    en: 'Times vary by how you travel.',
    de: 'Die Dauer hängt vom Verkehrsmittel ab.',
  },
  // A pinned area: nobody is travelling from anywhere we know, so there is nothing to
  // balance. One number instead of a comparison - it makes no claim about
  // fairness, which is right, because none was asked for.
  'fair.yourTrip': { en: 'About {minutes} min from you', de: 'Etwa {minutes} Min. von dir' },
  'fair.chosenArea': {
    en: 'In the area you chose',
    de: 'In dem Bezirk, den du gewählt hast',
  },

  // ── reject chips ──────────────────────────────────────────────────────
  // Asked once, on the way out - not under the cards, where they read as
  // "show me others" while actually spending a rejection.
  //
  // Written for a SET, not a single card: with three on screen "zu weit" asks
  // which one. And phrased as reasons rather than requests - "somewhere
  // quieter" asks us to try again, which is no longer what this question is
  // for. He is leaving; this is why.
  'chip.too_far': { en: 'Too far', de: 'Zu weit weg' },
  'chip.too_pricey': { en: 'Too pricey', de: 'Zu teuer' },
  'chip.too_loud': { en: 'Too loud', de: 'Zu laut' },
  'chip.not_my_thing': { en: 'Not my thing', de: 'Nicht mein Ding' },
  'chip.prompt': { en: 'Why not?', de: 'Warum nicht?' },
  'chip.leavePrompt': {
    en: 'Why weren’t these what you expected?',
    de: 'Warum waren das nicht die passenden Vorschläge?',
  },
  'chip.noReason': { en: 'Just leave', de: 'Einfach schließen' },

  // ── confirmed ─────────────────────────────────────────────────────────
  'done.title': { en: 'Sorted.', de: 'Sorted.' },
  'done.tell': { en: 'Tell them', de: 'Bescheid geben' },
  'done.directions': { en: 'Directions', de: 'Route' },
  // ── add to calendar ───────────────────────────────────────────────────
  // Asked rather than assumed: a bare .ics on Android lands in the
  // notification shade, so someone who tapped "add to calendar" sees no
  // calendar and concludes nothing happened.
  'cal.where': { en: 'Where should it go?', de: 'Wohin soll es?' },
  'cal.google': { en: 'Google Calendar', de: 'Google Kalender' },
  'cal.googleHint': { en: 'Opens in a new tab', de: 'Öffnet in einem neuen Tab' },
  // Named by what people have rather than by the file format: "Apple, Outlook
  // & others" is recognisable, ".ics" is not.
  'cal.ics': { en: 'Apple, Outlook & others', de: 'Apple, Outlook & andere' },
  'cal.icsHint': {
    en: 'Downloads a calendar file (.ics)',
    de: 'Lädt eine Kalenderdatei (.ics)',
  },
  'cal.cancel': { en: 'Cancel', de: 'Abbrechen' },

  'done.calendar': { en: 'Add to calendar', de: 'Zum Kalender' },
  'done.call': { en: 'Call to book', de: 'Anrufen & reservieren' },
  'done.pitch': {
    en: 'Need to sort out a meeting yourself?',
    de: 'Willst du selbst ein Treffen ausmachen?',
  },
  'done.pitchCta': { en: 'Create a link', de: 'Link erstellen' },
  'done.pending': { en: 'The details are on the way.', de: 'Die Details kommen gleich.' },

  // ── waiting (creator) ─────────────────────────────────────────────────
  'wait.picking': {
    en: 'The recipient is picking a place',
    de: 'Der Empfänger sucht gerade einen Ort aus',
  },
  'wait.unopened': {
    en: 'Waiting for them to open it',
    de: 'Warte, bis der Link geöffnet wird',
  },
  'wait.pickingHint': {
    en: 'You’ll see the place here as soon as they choose. Nothing for you to do.',
    de: 'Sobald der Ort feststeht, siehst du ihn hier. Du musst nichts tun.',
  },
  'wait.unopenedHint': {
    en: 'Send them the link if you haven’t already.',
    de: 'Schick den Link weiter, falls noch nicht geschehen.',
  },
  'wait.optOut': { en: 'Rather sort it directly?', de: 'Lieber direkt ausmachen?' },
  'wait.optOutWarn': {
    en: 'This ends the link and hands over to a normal chat.',
    de: 'Das beendet den Link und ihr macht es im Chat aus.',
  },
  'wait.keepWaiting': { en: 'Keep waiting', de: 'Weiter warten' },
  'wait.sortDirectly': { en: 'Sort it directly', de: 'Direkt ausmachen' },
  'wait.handoffTitle': { en: 'Over to you', de: 'Jetzt du' },
  'wait.handoffBody': {
    en: 'Message them and agree a place between you.',
    de: 'Schreib direkt und macht euch einen Ort aus.',
  },
  'wait.openWhatsapp': { en: 'Open WhatsApp', de: 'WhatsApp öffnen' },

  // ── errors ────────────────────────────────────────────────────────────
  'error.title': { en: 'Something went wrong', de: 'Da ist etwas schiefgelaufen' },
  'error.hint': {
    en: 'Not your fault - the link is fine. Try again in a moment.',
    de: 'Nicht deine Schuld - der Link ist in Ordnung. Probier es gleich nochmal.',
  },
  'error.retry': { en: 'Try again', de: 'Nochmal versuchen' },

  // ── terminal states ───────────────────────────────────────────────────
  'gone.expired': { en: 'This link has expired', de: 'Dieser Link ist abgelaufen' },
  'gone.expiredHint': { en: 'Ask them to send a new one.', de: 'Bitte um einen neuen Link.' },
  'gone.abandoned': { en: 'Sort it out directly', de: 'Macht es direkt aus' },
  // Either side can end it, so this cannot be written from one perspective.
  'gone.abandonedHint': {
    en: 'Agree a place between you instead.',
    de: 'Macht euch den Ort direkt untereinander aus.',
  },

  // ── activities ────────────────────────────────────────────────────────
  'activity.coffee': { en: 'Coffee', de: 'Kaffee' },
  'activity.brunch': { en: 'Brunch', de: 'Brunch' },
  'activity.drinks': { en: 'Drinks', de: 'Drinks' },
  'activity.dinner': { en: 'Dinner', de: 'Essen' },
  'activity.walk': { en: 'A walk', de: 'Spaziergang' },
  'activity.beer_garden': { en: 'Beer garden', de: 'Schanigarten' },

  // ── landmark kinds ────────────────────────────────────────────────────
  'kind.station': { en: 'station', de: 'Station' },
  'kind.district': { en: 'district', de: 'Bezirk' },
  'kind.town': { en: 'town', de: 'Ort' },
} as const

export type StringKey = keyof typeof STRINGS

/**
 * Every key, for the coverage test.
 *
 * Exported so the test cannot drift: a hand-maintained list in the test file
 * only ever covers the strings someone remembered to add to it, which is
 * exactly the strings least likely to be missing a translation.
 */
export const STRING_KEYS = Object.keys(STRINGS) as StringKey[]

function detect(): Locale {
  const stored = safeGet('sorted.locale')
  if (stored === 'en' || stored === 'de') return stored
  // navigator.languages is ordered by preference; take the first we support.
  for (const tag of navigator.languages ?? [navigator.language]) {
    if (tag?.toLowerCase().startsWith('de')) return 'de'
    if (tag?.toLowerCase().startsWith('en')) return 'en'
  }
  return 'en'
}

let locale: Locale = detect()

export function getLocale(): Locale {
  return locale
}

export function setLocale(next: Locale): void {
  locale = next
  try {
    localStorage.setItem('sorted.locale', next)
  } catch {
    /* private mode */
  }
  document.documentElement.lang = next
}

/**
 * Translate. `{name}` placeholders are substituted from `vars`.
 *
 * Missing keys are impossible by construction - `StringKey` is derived from
 * the table, so a typo is a type error rather than a key leaking to a user.
 */
export function t(key: StringKey, vars?: Record<string, string>): string {
  const entry = STRINGS[key] as Record<Locale, string>
  let out = entry[locale] ?? entry.en
  if (vars) {
    for (const [k, v] of Object.entries(vars)) out = out.replaceAll(`{${k}}`, v)
  }
  return out
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

// Set once at load so screen readers and `:lang()` rules see the right value.
if (typeof document !== 'undefined') document.documentElement.lang = locale
